import type { Polyline } from "@tbm/shared";

export interface PolylineSample {
  x: number;
  y: number;
  /** Tangent angle (radians) at this position; useful for perpendicular ticks. */
  angle: number;
  /** Cumulative arc length up to this position. */
  s: number;
}

/**
 * Cumulative arc-length array. cumulative[i] = total length up to point i.
 * Lengths are in the polyline's own coordinate space (which we use
 * normalised 0..1 image coords).
 */
export function cumulativeLengths(p: Polyline): number[] {
  const out = new Array<number>(p.length);
  let s = 0;
  out[0] = 0;
  for (let i = 1; i < p.length; i++) {
    const dx = p[i]![0] - p[i - 1]![0];
    const dy = p[i]![1] - p[i - 1]![1];
    s += Math.hypot(dx, dy);
    out[i] = s;
  }
  return out;
}

export function arcLength(p: Polyline): number {
  if (p.length < 2) return 0;
  return cumulativeLengths(p)[p.length - 1] ?? 0;
}

/**
 * Sample the polyline at fraction f in [0, 1] of its total arc length.
 * Returns null if the polyline has fewer than 2 points.
 */
export function interpolateAt(p: Polyline, fraction: number): PolylineSample | null {
  if (p.length < 2) return null;
  const f = Math.max(0, Math.min(1, fraction));
  const cum = cumulativeLengths(p);
  const total = cum[cum.length - 1] ?? 0;
  if (total === 0) {
    const pt = p[0]!;
    return { x: pt[0], y: pt[1], angle: 0, s: 0 };
  }
  const target = f * total;
  // Find segment via binary search.
  let lo = 0,
    hi = cum.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid]! <= target) lo = mid;
    else hi = mid;
  }
  const a = p[lo]!;
  const b = p[hi]!;
  const segLen = (cum[hi] ?? 0) - (cum[lo] ?? 0);
  const t = segLen === 0 ? 0 : (target - (cum[lo] ?? 0)) / segLen;
  const x = a[0] + (b[0] - a[0]) * t;
  const y = a[1] + (b[1] - a[1]) * t;
  const angle = Math.atan2(b[1] - a[1], b[0] - a[0]);
  return { x, y, angle, s: target };
}

/**
 * N evenly-spaced positions along the polyline (excluding the very ends if
 * needed). Used for chainage tick marks.
 */
export function tickPositions(p: Polyline, n: number): PolylineSample[] {
  if (p.length < 2 || n < 1) return [];
  const out: PolylineSample[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const s = interpolateAt(p, f);
    if (s) out.push(s);
  }
  return out;
}

/**
 * Tick positions every `intervalM` metres of chainage along a polyline that
 * spans 0..driveLengthM. Returns positions and the chainage value at each.
 * The first tick is at intervalM (not 0) and the last is the largest multiple
 * < driveLengthM.
 */
export function chainageTicks(
  p: Polyline,
  driveLengthM: number,
  intervalM: number,
): Array<PolylineSample & { chainageM: number }> {
  if (p.length < 2 || driveLengthM <= 0 || intervalM <= 0) return [];
  const out: Array<PolylineSample & { chainageM: number }> = [];
  for (let c = intervalM; c < driveLengthM; c += intervalM) {
    const f = c / driveLengthM;
    if (f < 0 || f > 1) continue;
    const s = interpolateAt(p, f);
    if (s) out.push({ ...s, chainageM: c });
  }
  return out;
}
